import { hasPermission } from "@saleor/auth/misc";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import { PayoutStatusEnum, PermissionEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import React from "react";
import { useIntl } from "react-intl";

import { ReportsUrl } from "../../../overview/urls";
import PayoutScheduleDialog, {
  PayoutFormData
} from "../../components/PayoutScheduleDialog/PayoutScheduleDialog";
import PayoutListPage from "../../components/PayoutsListPage";
import { usePayoutScheduleMutation } from "../../mutation";
import { usePayoutScheduleLatestQuery } from "../../queries";
import {
  useInitialPayoutFilterDataQuery,
  usePayoutListQuery
} from "../../queries";
import {
  payoutDetailsUrl,
  payoutsListUrl,
  PayoutsListUrlDialog,
  PayoutsUrlQueryParams
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface PayoutListProp {
  params: PayoutsUrlQueryParams;
}

const PayoutsList: React.FC<PayoutListProp> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const shop = useShop();
  const intl = useIntl();
  const { user } = useUser();
  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);

  const currencySymbol = shop?.defaultCurrency || "PHP";
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),

    [params]
  );
  const { data: initialFilterData } = useInitialPayoutFilterDataQuery({
    variables: {
      brandId: params.brand,
      mall: params.mall
    }
  });

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const searchBrands = useBrandSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const tabs = [
    ...[
      {
        data: {
          payoutStatus: PayoutStatusEnum.SCHEDULE_FOR_PAYOUT
        },
        name: "Open Order",
        show: hasPermission(PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER, user)
      },
      {
        data: {
          payoutStatus: PayoutStatusEnum.ON_PROCESS
        },
        name: "On Process",
        show: hasPermission(PermissionEnum.MANAGE_PAYOUT_ON_PROCESS, user)
      },
      {
        data: {
          payoutStatus: PayoutStatusEnum.FOR_PAYMENT
        },
        name: "Bank File",
        show: hasPermission(PermissionEnum.MANAGE_PAYOUT_PAYABLES, user)
      },
      {
        data: {
          payoutStatus: PayoutStatusEnum.PAYMENT_PROCESSING
        },
        name: "Payment",
        show: hasPermission(PermissionEnum.MANAGE_PAYOUT_PAYMENT, user)
      },
      {
        data: {
          payoutStatus: PayoutStatusEnum.COMPLETED
        },
        name: "Completed",
        show: hasPermission(PermissionEnum.MANAGE_PAYOUT_PAYMENT, user)
      }
    ].filter(e => e.show),
    ...getFilterTabs()
  ];

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: payoutsListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    PayoutsListUrlDialog,
    PayoutsUrlQueryParams
  >(navigate, payoutsListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      payoutsListUrl({
        activeTab: tab.toString(),
        ...tabs[tab - 1]?.data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(payoutsListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };
  const { data: payoutDataSchedule } = usePayoutScheduleLatestQuery({
    displayLoader: false
  });

  const { data, loading, refetch } = usePayoutListQuery({
    displayLoader: true,
    variables: queryVariables
  });

  React.useEffect(
    () =>
      navigate(
        payoutsListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const userTab = tabs?.map(e => e.data.payoutStatus);
  React.useEffect(() => {
    if (!Object.values(userTab).includes(params.payoutStatus)) {
      navigate(payoutsListUrl());
    }
  }, [params.payoutStatus]);

  const handlePayoutSchedule = () => {
    notify({
      status: "success",
      text: intl.formatMessage({
        defaultMessage: "Payment Schedule updated!"
      })
    });
    closeModal();
    refetch();
  };
  const [payoutSchedule] = usePayoutScheduleMutation({
    onCompleted: handlePayoutSchedule
  });

  const handleSchedule = async (scheduleParams: PayoutFormData) => {
    const { payoutStartDate, payoutEndDate, scheduleDate } = scheduleParams;
    payoutSchedule({
      variables: {
        payoutSchedule: {
          coverageDate: {
            from: payoutStartDate,
            to: payoutEndDate
          },
          schedule: scheduleDate
        }
      }
    });
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    data?.payouts?.pageInfo,
    paginationState,
    params
  );

  const filterOpts = getFilterOpts(
    params,
    {
      initial: maybe(
        () => initialFilterData.malls.edges.map(edge => edge.node) || []
      ),
      search: searchMall
    },
    {
      initial: maybe(
        () => initialFilterData.brands.edges.map(edge => edge.node) || []
      ),
      search: searchBrands
    }
  );

  const PayoutsData = data?.payouts?.edges.map(edge => edge.node);
  const hideAction = params.payoutStatus ? true : false;

  return (
    <>
      <PayoutListPage
        params={params}
        payouts={PayoutsData}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        onSchedule={() => openModal("schedule")}
        tabs={tabs.map(tab => tab.name)}
        disabled={loading}
        pageInfo={pageInfo}
        hideAction={hideAction}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onBack={() => navigate(ReportsUrl)}
        onRowClick={id => () => navigate(payoutDetailsUrl(id))}
      />

      <PayoutScheduleDialog
        open={params?.action === "schedule"}
        onClose={closeModal}
        payoutDataSchedule={payoutDataSchedule}
        onSubmit={handleSchedule}
      ></PayoutScheduleDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default PayoutsList;
